import { useState, useEffect, useCallback, useMemo } from 'react';
import { useMatch, useParams } from 'react-router';

import CommentsPanel from '@/components/comments/CommentsPanel';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { controlMachine, controlMachineConfig } from '@/config/controlStates';
import { CONTROL_DEFAULT_STATE } from '@/constants';
import { ControlContext } from '@/contexts/ControlContext';
import { useAuditSnapshot } from '@/hooks/useAuditSnapshot';
import useCognitoUsers from '@/hooks/useCognitoUsers';
import { normalizeControl, useControlById } from '@/hooks/useControlsAPI';
import { DETAIL_MODE, DETAIL_TYPE, useDetailMode } from '@/hooks/useDetailMode';
import PageLayout from '@/layouts/PageLayout';
import { getUserInfo } from '@/lib/auth';
import { apiService } from '@/lib/axios';
import { deriveRiskGrading } from '@/lib/riskAssessment';
import { toApprovalsRows } from '@/lib/utils';
import ControlApprovalsView from '@/views/controls/ControlApprovalsView';
import ControlDetailsView from '@/views/controls/ControlDetailsView';
import ControlInformationView from '@/views/controls/ControlInformationView';
import ControlRiskView from '@/views/controls/ControlRiskView';

import { ControlActionsHead, ControlActionsTail } from './ControlActions';

const MASTER_LIST_STATUSES = ['COMPLETE', 'TRAINING_IN_PROGRESS'];
const COMMENT_TABS = [
  {
    key: 'information',
    title: 'Comments for Change Control Information',
  },
  {
    key: 'details',
    title: 'Comments for Details of Change',
  },
  {
    key: 'risk',
    title: 'Comments for Risk Impact Assessment',
  },
];

const ControlDetailPage = () => {
  const { id } = useParams();
  const [form, setForm] = useState(CONTROL_DEFAULT_STATE);
  const [activeTab, setActiveTab] = useState('information');
  const [reviews, setReviews] = useState([]);
  const [canApprove, setCanApprove] = useState(false);

  const matchNew = useMatch('/control/new');

  const { isSnapshotMode, isMasterPreview, mode } = useDetailMode(
    DETAIL_TYPE.CONTROL
  );
  const { snapshotData, snapshotComments, isLoading: snapshotLoading, error: snapshotError } =
    useAuditSnapshot();
  const { control: currentControl } = useControlById(
    isSnapshotMode ? null : id
  );
  const control = useMemo(
    () =>
      isSnapshotMode && snapshotData
        ? normalizeControl(snapshotData)
        : currentControl,
    [currentControl, isSnapshotMode, snapshotData]
  );

  // ── Cognito users for designation lookup ──────
  const { users: cognitoUsers = [] } = useCognitoUsers();
  const usersMap = useMemo(
    () => Object.fromEntries(cognitoUsers.map((u) => [u.sub, u])),
    [cognitoUsers]
  );

  const reviewerRole = useMemo(() => {
    const reviewGroupOrder = controlMachineConfig.reviewGroupOrder || [];
    const reviewers = control?.reviewers;
    const currentActorIndex = control?.currentActorIndex ?? 0;

    if (!reviewers || !reviewGroupOrder.length) return null;

    const currentUserSub = getUserInfo()?.sub;

    if (!currentUserSub) return null;

    const pipeline =
      control?.reviewerPipeline ||
      reviewGroupOrder.flatMap((group) => reviewers[group] || []);
    const current = pipeline[currentActorIndex];
    const currentSub = typeof current === 'object' ? current?.sub : current;

    if (currentUserSub !== currentSub) return null;

    let index = 0;

    for (const group of reviewGroupOrder) {
      const groupMembers = reviewers[group] || [];
      if (
        currentActorIndex >= index &&
        currentActorIndex < index + groupMembers.length
      ) {
        const roleMap = {
          processOwners: 'Process Owner',
          qmrQa: 'QA',
          managementApprovers: 'MGMT',
        };
        return roleMap[group] || group;
      }
      index += groupMembers.length;
    }
    return null;
  }, [
    control?.reviewers,
    control?.reviewerPipeline,
    control?.currentActorIndex,
  ]);

  const setFormByKey = useCallback((key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const commentEntityRevision = useMemo(() => {
    if (
      control?.documentRevision === null ||
      control?.documentRevision === undefined
    )
      return undefined;

    const revision = Number(control.documentRevision);

    return Number.isFinite(revision) ? revision : undefined;
  }, [control?.documentRevision]);

  useEffect(() => {
    if (isSnapshotMode) {
      setForm(control ? {
        ...CONTROL_DEFAULT_STATE,
        ...control,
        serial: control.serial || '',
        requestDate: control.createdAt || '',
        preparedBy: control.requestorName || '',
      } : CONTROL_DEFAULT_STATE);
    }
  }, [isSnapshotMode, control]);

  useEffect(() => {
    if (!control?.reviewers) return;

    const { qmrQa, processOwners, managementApprovers } = control.reviewers;

    setForm((prev) => ({
      ...prev,
      approvalsAssigned: qmrQa?.length
        ? toApprovalsRows(qmrQa, usersMap)
        : undefined,
      approvalsOwner: processOwners?.length
        ? toApprovalsRows(processOwners, usersMap)
        : undefined,
      approvalsPersonnel: managementApprovers?.length
        ? toApprovalsRows(managementApprovers, usersMap)
        : undefined,
    }));
  }, [control, usersMap]);

  useEffect(() => {
    if (control && !matchNew && !form.id) {
      setForm((prev) => ({
        ...prev,
        ...control,
        serial: control.serial || '',
        requestDate: control.createdAt || '',
        preparedBy: control.requestorName || '',
      }));
    }
  }, [control, form, matchNew]);

  useEffect(() => {
    setFormByKey('riskGrading', deriveRiskGrading(form.riskScore));
  }, [form.riskScore, setFormByKey]);

  useEffect(() => {
    if (isSnapshotMode) {
      setReviews(
        (snapshotComments || []).map((comment) => ({
          ...comment,
          updateAt: comment.updateAt || comment.updatedAt,
        }))
      );
      return;
    }

    const canLoadComments =
      mode === DETAIL_MODE.STATIC ||
      (mode === DETAIL_MODE.EDIT && form.status === 'RETURNED');

    if (!id || !canLoadComments) return;

    apiService.comments
      .getAll({
        entityType: 'Control',
        entityId: id,
        entityRevision: commentEntityRevision,
      })
      .then((res) => {
        if (!res.data.success) return;

        const loadedReviews = res.data.data.map((comment) => ({
          id: comment.id,
          tab: comment.tab,
          text: comment.text,
          updateAt: comment.updatedAt,
          entityType: comment.entityType,
          entityId: comment.entityId,
          entityRevision: comment.entityRevision,
          resolvedAt: comment.resolvedAt,
          frozenAt: comment.frozenAt,
          authorSub: comment.authorSub,
          authorName: comment.authorName,
        }));

        setReviews((prev) => {
          const savedIds = new Set(loadedReviews.map((review) => review.id));
          return [
            ...loadedReviews,
            ...prev.filter((review) => !savedIds.has(review.id)),
          ];
        });
      })
      .catch(() => {});
  }, [
    id,
    mode,
    commentEntityRevision,
    isSnapshotMode,
    snapshotComments,
    form.status,
  ]);

  // Determine if form is read-only based on mode, status or explicit preview state
  const readOnly =
    mode === DETAIL_MODE.STATIC ||
    (form.status && MASTER_LIST_STATUSES.includes(form.status)) ||
    isMasterPreview;
  const showCommentsPanel =
    activeTab !== 'approvals' &&
    activeTab !== 'summary' &&
    (mode === DETAIL_MODE.STATIC ||
      (mode === DETAIL_MODE.EDIT && form.status === 'RETURNED')) &&
    !isMasterPreview &&
    (!reviewerRole ? reviews.some((review) => review.tab === activeTab) : true);
  const blockingCommentsCount = useMemo(() => {
    const currentSub = getUserInfo()?.sub;

    return reviews.filter(
      (review) => !review.resolvedAt && review.authorSub === currentSub
    ).length;
  }, [reviews]);
  const userInfo = getUserInfo();
  const reviewerPipeline =
    form.reviewerPipeline ||
    controlMachineConfig.reviewGroupOrder.flatMap(
      (group) => form.reviewers?.[group] || []
    );
  const allowedActions =
    !isSnapshotMode && form.status
      ? controlMachine.getActions(
          form.status,
          userInfo?.sub,
          reviewerPipeline,
          form.currentActorIndex ?? 0,
          form.requestorId,
          userInfo?.['cognito:groups']
        )
      : [];

  if (isSnapshotMode && (snapshotLoading || snapshotError)) {
    return (
      <PageLayout title="Audit Trail Snapshot">
        <p role={snapshotError ? 'alert' : 'status'}>
          {snapshotError
            ? 'Unable to load the historical snapshot. Please try again.'
            : 'Loading historical snapshot...'}
        </p>
      </PageLayout>
    );
  }

  return (
    <ControlContext.Provider
      value={{
        activeTab,
        form,
        id,
        readOnly,
        reviews,
        setActiveTab,
        setFormByKey,
      }}
    >
      <PageLayout
        title={
          mode === DETAIL_MODE.STATIC
            ? `Change Control Form: ${form.serial}`
            : mode === DETAIL_MODE.EDIT
              ? `Edit Change Control Form: ${form.serial}`
              : 'Raise a Change Control Form'
        }
        toolbarHead={isSnapshotMode ? null : <ControlActionsHead />}
        toolbarTail={
          !isSnapshotMode && (
            <ControlActionsTail
              allowedActions={allowedActions}
              blockingCommentsCount={blockingCommentsCount}
              canApprove={canApprove}
              reviews={reviews}
            />
          )
        }
      >
        {isSnapshotMode && (
          <div className="mb-6 rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
            Historical snapshot from the audit trail. This view is read-only.
          </div>
        )}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full flex mb-6 gap-x-2 gap-y-1 flex-wrap">
            <TabsTrigger value="information">
              Change Control Information
            </TabsTrigger>
            <TabsTrigger value="details">Details of Change</TabsTrigger>
            <TabsTrigger value="risk">Risk Impact Assessment</TabsTrigger>
            <TabsTrigger value="approvals">Approvals</TabsTrigger>
            {(mode === DETAIL_MODE.STATIC ||
              (mode === DETAIL_MODE.EDIT && form.status === 'RETURNED')) &&
              form.status !== 'DRAFT' && (
                <TabsTrigger value="summary">Review Summary</TabsTrigger>
              )}
          </TabsList>

          <TabsContent value="information" className="mt-0">
            <ControlInformationView />
          </TabsContent>

          <TabsContent value="details" className="mt-0">
            <ControlDetailsView />
          </TabsContent>

          <TabsContent value="risk" className="mt-0">
            <ControlRiskView />
          </TabsContent>

          <TabsContent value="approvals" className="mt-0 space-y-8">
            <ControlApprovalsView />
          </TabsContent>

          <TabsContent value="summary" className="mt-0">
            <CommentsPanel
              activeTab="summary"
              canSubmit={Boolean(
                !isSnapshotMode &&
                  reviewerRole &&
                  control?.status === 'REVIEWING'
              )}
              currentUserSub={getUserInfo()?.sub}
              entityId={id}
              entityRevision={commentEntityRevision}
              entityType="Control"
              reviewerRole={reviewerRole}
              reviews={reviews}
              setCanApprove={setCanApprove}
              setReviews={setReviews}
              tabs={COMMENT_TABS}
            />
          </TabsContent>
        </Tabs>
        {showCommentsPanel && (
          <CommentsPanel
            activeTab={activeTab}
            canSubmit={Boolean(
              !isSnapshotMode && reviewerRole && control?.status === 'REVIEWING'
            )}
            className="mt-6"
            currentUserSub={getUserInfo()?.sub}
            entityId={id}
            entityRevision={commentEntityRevision}
            entityType="Control"
            reviewerRole={reviewerRole}
            reviews={reviews}
            setCanApprove={setCanApprove}
            setReviews={setReviews}
            tabs={COMMENT_TABS}
          />
        )}
      </PageLayout>
    </ControlContext.Provider>
  );
};

export default ControlDetailPage;
